
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BarChart, Book, Building2, Factory, GraduationCap, Heart, Home, Landmark, ShieldAlert, Briefcase, Users } from 'lucide-react';

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  active: boolean;
  icon?: React.ComponentType<any>;
}

// Map category names to their corresponding icons
const categoryIcons: Record<string, React.ComponentType<any>> = {
  'Economy': Briefcase,
  'Healthcare': Heart,
  'Education': GraduationCap,
  'Housing': Home,
  'Industry': Factory,
  'Justice': ShieldAlert,
  'Government': Landmark,
  'Social': Users,
  'Infrastructure': Building2,
  'Culture': Book,
  'Default': BarChart,
};

export const useCategories = (activeOnly = true) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        let query = supabase
          .from('insight_categories')
          .select('*')
          .order('name');
          
        if (activeOnly) {
          query = query.eq('active', true);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Add icons to categories
        const categoriesWithIcons = (data || []).map(category => ({
          ...category,
          icon: categoryIcons[category.name] || categoryIcons.Default
        }));
        
        setCategories(categoriesWithIcons);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(error instanceof Error ? error : new Error('Failed to fetch categories'));
        toast.error('Failed to load categories');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [activeOnly]);

  return { categories, isLoading, error };
};

export default useCategories;
